import {
  ProcessImportBatchItemResult,
  ProcessImportBatchOutcome,
} from "@via/api/process-git-import.api.tsx";
import { JSX } from "react";

/**
 * The last step: what the batch actually did, one row per requested revision.
 *
 * It replaces the single import's success toast, because a batch can legitimately end with some
 * processes imported and others not. Divergence, the uuid race and the classifier re-check all run
 * at apply time, so a user can consent to twenty five processes and receive twenty three, and
 * this step is where that is said plainly rather than hidden behind a green toast.
 *
 * Grouped by outcome with imported first, whatever order the response lists them in (the
 * response keeps request order). Every row names its process as text: the frontend has no route
 * for a single process, so there is nothing honest to link to. An imported row uses the name the
 * server created it under; a skipped or failed row carries no name in the response, so it uses the
 * name from `processNames`, which the modal builds from the scan rows the user selected (repoPath
 * is unique within a batch, so it is a safe key). A skipped or failed row shows the server's
 * message verbatim. The backend already sanitises a failed item's message, so nothing here
 * rewords or filters it.
 */

const GROUPS: { outcome: ProcessImportBatchOutcome; heading: string }[] = [
  { outcome: "imported", heading: "Imported" },
  { outcome: "skipped", heading: "Skipped" },
  { outcome: "failed", heading: "Failed" },
];

export default function ResultsStep({
  results,
  processNames,
  onRunAgain,
  onDone,
}: {
  results: ProcessImportBatchItemResult[];
  processNames: ReadonlyMap<string, string>;
  onRunAgain: () => void;
  onDone: () => void;
}): JSX.Element {
  const total = results.length;
  const importedCount = results.filter(
    result => "imported" === result.outcome
  ).length;

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        <p className="font-medium text-gray-900">
          Imported {importedCount} of {total}{" "}
          {1 === total ? "process" : "processes"}.
        </p>
        {importedCount > 0 ? (
          <p>Foundry stored what it imported without executing it.</p>
        ) : null}
        {importedCount < total ? (
          <p>
            Anything not imported is listed below with the reason the server
            gave.
          </p>
        ) : null}
      </div>

      {GROUPS.map(({ outcome, heading }) => {
        const group = results.filter(result => outcome === result.outcome);
        if (0 === group.length) {
          return null;
        }
        return (
          <section key={outcome} className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">
              {heading} ({group.length})
            </h3>
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {group.map((result, idx) => (
                <li
                  key={`${result.repoPath}:${result.revision}:${idx}`}
                  className="px-3 py-2 text-sm">
                  <p className="font-medium text-gray-900">
                    {result.name ??
                      processNames.get(result.repoPath) ??
                      result.repoPath}
                  </p>
                  <p className="text-xs text-gray-500">
                    {result.repoPath}, revision {result.revision}
                    {"imported" === outcome && null !== result.processId
                      ? `, process #${result.processId}`
                      : ""}
                  </p>
                  {"imported" !== outcome && null !== result.message ? (
                    <p className="mt-1 text-xs text-gray-600">
                      {result.message}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid="importRunAgain"
          onClick={onRunAgain}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
          Run again
        </button>
        <button
          type="button"
          data-testid="importResultsDone"
          onClick={onDone}
          className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
          Done
        </button>
      </div>
    </div>
  );
}
